import React, { useEffect, useState } from "react";
import styles from './Profile.module.scss';
import PageTitle from "../../components/pageTitle/PageTitle";
import ProfileUpdateForm from "../../components/profileUpdateForm/ProfileUpdateForm";
import axiosClient from "../../api/axiosClient";
import LoadingOverlay from "../../components/loadingOverlay/LoadingOverlay";
import { useAuth } from "../../contexts/AuthContext";
import { useToasts } from "../../contexts/ToastContext";
import Toggle from "../../components/toggle/Toggle";

const Profile = () => {
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    healthSystemIds: [],
    clinicalSitesIds: [],
    speciality: '',
    specialityIds: []
  });
  const [clinicalSiteList, setClinicalSiteList] = useState([]);
  const [healthSystemList, setHealthSystemsList] = useState([]);
  const [specialityList, setSpecialityList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [subscriptionId, setSubscrptionId] = useState(null);
  const [program, setProgram] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  const { setUser, role } = useAuth();
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`users/profile`)
      .then ((response) => {
        let profileData = {
          firstName: response.data?.firstName,
          lastName: response.data?.lastName,
          email: response.data?.email,
          mobileNumber: response.data?.mobileNumber,
          profilePicture: response.data?.profilePicture
        }
        if(response.data?.healthSystems) {
          profileData.healthSystemIds = response.data.healthSystems.map((item) => item.id);
        }
        if(response.data?.clinicalSites) {
          profileData.clinicalSiteIds = response.data.clinicalSites.map((item) => item.id);
        }
        if(response.data?.speciality) {
          profileData.speciality = response.data.speciality;
        }
        if(response.data?.specialities) {
          profileData.specialityIds = response.data.specialities.map((item) => item.id);
        }
        if(response.data?.clinicalSitePreferences) {
          profileData.clinicalSitePreferences = response.data.clinicalSitePreferences
            .sort((a, b) => a.order - b.order)
            .map(p => ({ id: p.clinicalSite.id, name: p.clinicalSite.name }));
        }
        if(response.data?.specialityPreferences) {
          profileData.specialityPreferences = response.data.specialityPreferences
            .sort((a, b) => a.order - b.order)
            .map(p => ({ id: p.speciality.id, name: p.speciality.name }));
        }
        setProfileData((prevData) => {
          return {
            ...prevData,
            ...profileData
          }
        });
        if(response.data?.subscription) {
          setSubscrptionId(response.data.subscription?.id)
        }
        if(response.data?.program) {
          setProgram(response.data.program);
        }
        if(response.data?.isAvailable !== undefined) {
          setIsAvailable(response.data.isAvailable);
        }
        axiosClient.get('specialities')
        .then ((specialityResponse) => {
          setSpecialityList(specialityResponse.data.map((item) => ({
            value: item.id,
            label: item.name
          })));
        });
      })
      .catch((error) => {
        setError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`health-systems/subscription/${subscriptionId}`)
      .then((response) => {
        setHealthSystemsList(response.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
      })
    };

    if(subscriptionId) {
      fetchData();
    }
  }, [subscriptionId]);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`clinical-sites`, {
        params: {
          healthSystemIds: profileData.healthSystemIds.join(',')
        }
      })
      .then((response) => {
        setClinicalSiteList(response.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
        setProfileData((prevData) => {
          const existingClinicalSiteIds = prevData.clinicalSiteIds;
          const availableClinicalSiteIds = response.data.map(item => item.id);
          const filteredClinicalSiteIds = 
            existingClinicalSiteIds.filter(id => availableClinicalSiteIds.includes(id));
          return {
            ...prevData,
            clinicalSiteIds: filteredClinicalSiteIds
          } 
        });
      })
    };

    if(profileData.healthSystemIds.length > 0) {
      fetchData();
    } else {
      setClinicalSiteList([]);
      setProfileData((prevData) => {
        return {
          ...prevData,
          clinicalSiteIds: []
        }
      });
    }
  }, [profileData.healthSystemIds]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleToggleAvailability = async () => {
    const newValue = !isAvailable;
    setIsAvailable(newValue);
    setIsAvailabilityLoading(true);
    try {
      const response = await axiosClient.patch(`users/profile/student/availability`, { isAvailable: newValue });
      showToast(response.message, 'success');
    } catch (err) {
      setIsAvailable(!newValue);
      showToast(err.message, 'error');
    } finally {
      setIsAvailabilityLoading(false);
    }
  };

  const handleProfileUpdate = async (data, imageFile) => {
    const formData = new FormData();
    for (let key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (Array.isArray(data[key])) {
          data[key].forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              Object.keys(item).forEach(subKey => {
                formData.append(`${key}[${index}][${subKey}]`, item[subKey]);
              });
            } else {
              formData.append(`${key}[]`, item);
            }
          });
        } else {
          formData.append(key, data[key]);
        }
      }
    }
    if(imageFile) {
      formData.append('profilePicture', imageFile);
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    await axiosClient.patch(`users/profile`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    })
    .then((response) => {
      setSuccess(response.message);
      setUser(response.data);
    })
    .catch((error) => {
      setError(error.message);
    })
    .finally(() => {
      setIsLoading(false);
      scrollToTop();
    });
  };

  return (
    <div className={styles.container}>
      {isLoading && <LoadingOverlay />}
      <div className={styles.titleRow}>
        <PageTitle
          label="Update Profile"
          menu="profile"
        />
        {role === 'Student' && (
          <Toggle
            checked={isAvailable}
            onChange={handleToggleAvailability}
            label={isAvailable ? 'Available' : 'Unavailable'}
            disabled={isAvailabilityLoading}
          />
        )}
      </div>
      <ProfileUpdateForm
        formData={profileData}
        healthSystemList={healthSystemList}
        onHealthSystemChange={(healthSystemIds) => {
          setProfileData((prevData) => {
            return {
              ...prevData,
              healthSystemIds: healthSystemIds
            }
          });
        }}
        clinicalSiteList={clinicalSiteList}
        specialityList={specialityList}
        onSubmit={handleProfileUpdate}
        error={error}
        success={success}
        program={program}
      />
    </div>
  );
};

export default Profile;